


















































































import { Component, Prop, Watch } from 'vue-property-decorator';
import Vue from 'vue';
import { TaskPriority, TaskPriorityLabels, TaskStatus } from 'admiral-core';
import { CheckIcon, TrashIcon } from 'vue-feather-icons';
import TaskStatusIcon from './TaskStatusIcon.vue';
import TaskPriorityIcon from './TaskPriorityIcon.vue';
import { TaskApi } from '@/services/TaskApi';
import type { TaskListSingletonMenuState } from './TaskListSingletonMenuState';

const STATUS_LABELS: Record<TaskStatus, string> = {
  [TaskStatus.TODO]: 'Offen',
  [TaskStatus.IN_PROGRESS]: 'In Bearbeitung',
  [TaskStatus.IN_WAITING]: 'Warten',
  [TaskStatus.COMPLETED]: 'Erledigt',
  [TaskStatus.ARCHIVED]: 'Archiviert',
};

@Component({
  name: 'TaskListSingletonMenus',
  components: {
    TaskStatusIcon,
    TaskPriorityIcon,
    CheckIcon,
    TrashIcon,
  },
})
export default class TaskListSingletonMenus extends Vue {
  @Prop({ type: Object, required: true })
  state!: TaskListSingletonMenuState;

  panelTop = 0;
  panelLeft = 0;

  private readonly onScrollCapture = () => this.measure();
  private readonly onResize = () => this.measure();
  private readonly onKeydown = (e: KeyboardEvent) => this.onDocKeydown(e);

  mounted() {
    document.body.appendChild(this.$el);
  }

  get statusOptions(): { status: TaskStatus; label: string }[] {
    return Object.values(TaskStatus).map((status) => ({
      status,
      label: STATUS_LABELS[status],
    }));
  }

  get priorityOptions(): { priority: TaskPriority; label: string }[] {
    return Object.values(TaskPriority).map((priority) => ({
      priority,
      label: TaskPriorityLabels[priority],
    }));
  }

  get taskPriority(): TaskPriority {
    return this.state.task.priority ?? TaskPriority.NO_PRIORITY;
  }

  get panelStyle(): Record<string, string> {
    return {
      position: 'fixed',
      top: `${this.panelTop}px`,
      left: `${this.panelLeft}px`,
      zIndex: '2000',
      minWidth: '12rem',
      maxWidth: 'min(18rem, calc(100vw - 1rem))',
      padding: '0.25rem 0',
    };
  }

  @Watch('state', { immediate: true, deep: true })
  onStateChange() {
    this.$nextTick(() => {
      this.detachGlobalListeners();
      this.measure();
      window.addEventListener('scroll', this.onScrollCapture, true);
      window.addEventListener('resize', this.onResize);
      document.addEventListener('keydown', this.onKeydown);
    });
  }

  beforeDestroy() {
    this.detachGlobalListeners();
  }

  private detachGlobalListeners() {
    window.removeEventListener('scroll', this.onScrollCapture, true);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('keydown', this.onKeydown);
  }

  measure() {
    if (!this.state?.anchor || !document.body.contains(this.state.anchor)) {
      this.close();
      return;
    }
    const r = this.state.anchor.getBoundingClientRect();
    const gap = 4;
    let top = r.bottom + gap;
    let left = r.left;
    const panelW = 260;
    const panelH = 280;
    if (left + panelW > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - panelW - 8);
    }
    if (top + panelH > window.innerHeight - 8) {
      top = Math.max(8, r.top - panelH - gap);
    }
    this.panelTop = top;
    this.panelLeft = left;
  }

  onDocKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') this.close();
  }

  close() {
    this.$emit('close');
  }

  async onSelectStatus(newStatus: TaskStatus) {
    if (this.state.kind !== 'status') return;
    const { task } = this.state;
    this.close();
    if (task.status === newStatus) return;
    try {
      await TaskApi.updateStatus(task.id, task, newStatus);
      this.$set(task, 'status', newStatus);
      this.$emit('status-changed');
    } catch (e) {
      this.$toastr.e('Status konnte nicht geändert werden.');
    }
  }

  async onSelectPriority(newPriority: TaskPriority) {
    if (this.state.kind !== 'priority') return;
    const { task } = this.state;
    this.close();
    if (this.taskPriority === newPriority) return;
    try {
      await TaskApi.updatePriority(task.id, task, newPriority);
      this.$set(task, 'priority', newPriority);
      this.$emit('priority-changed');
    } catch (e) {
      this.$toastr.e('Priorität konnte nicht geändert werden.');
    }
  }

  async onDeleteTask() {
    if (this.state.kind !== 'status') return;
    const { task } = this.state;
    try {
      await TaskApi.delete(task.id);
      // Emit before close(): close clears parent `statusMenuState` and destroys this component
      // (v-if) — $emit after unmount does not reach @delete-task on TasksCalendar / TaskOverview.
      this.$emit('delete-task', task);
      this.close();
    } catch (e) {
      this.$toastr.e('Aufgabe konnte nicht gelöscht werden.');
    }
  }
}
